import {
  ELEMENT_TYPE_KEYS,
  LEGACY_ELEMENT_TYPE_MAP,
  LEGACY_STATUS_MAP,
  LEGACY_SYNC_STATUS_MAP,
} from '../constants/planning.constants';
import { createPlanningDraft } from '../models/planning.model';
import { minutesFromTime, normalizePlanningInterval, toDateKey } from './planningDates';

const normalizeKey = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, '_');

export const toFiniteNumber = (value, fallback = 0) => {
  const number = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
  return Number.isFinite(number) ? number : fallback;
};

export const toPlanningBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1') return true;
  if (value === 0 || value === '0') return false;
  const key = normalizeKey(value);
  if (['true', 'yes', 'oui'].includes(key)) return true;
  if (['false', 'no', 'non', ''].includes(key)) return false;
  return fallback;
};

export const normalizeEntityId = (value) => value === '' || value === undefined || value === null ? null : value;
export const sameEntityId = (first, second) => first != null && second != null && String(first) === String(second);

export const normalizeElementType = (value) => {
  const key = normalizeKey(value);
  if (ELEMENT_TYPE_KEYS.includes(key)) return key;
  return LEGACY_ELEMENT_TYPE_MAP[key] || 'event';
};

export const normalizePlanningStatus = (value) => LEGACY_STATUS_MAP[normalizeKey(value)] || 'planned';
export const normalizeSyncStatus = (value) => LEGACY_SYNC_STATUS_MAP[normalizeKey(value)] || 'not_synced';

export const normalizePlanningEvent = (raw = {}) => {
  const originalType = raw.elementType || raw.type || 'event';
  const elementType = normalizeElementType(originalType);
  const startDate = raw.startDate || raw.date || raw.eventDate || toDateKey(new Date());
  const isAllDay = toPlanningBoolean(raw.isAllDay ?? raw.is_all_day);
  const startTime = isAllDay ? '' : String(raw.startTime || raw.time || raw.eventTime || '09:00').slice(0, 5);
  const legacyDuration = String(raw.duration || '01:00');
  const durationMinutes = Math.max(0, toFiniteNumber(raw.durationMinutes ?? raw.duration_minutes, minutesFromTime(legacyDuration)));
  const start = new Date(`${startDate}T${startTime || '00:00'}:00`);
  const calculatedEnd = new Date(start.getTime() + durationMinutes * 60000);
  const endTimeFallback = `${String(calculatedEnd.getHours()).padStart(2, '0')}:${String(calculatedEnd.getMinutes()).padStart(2, '0')}`;

  const normalized = {
    ...createPlanningDraft(elementType, start),
    ...raw,
    legacyElementType: normalizeKey(originalType) !== elementType ? originalType : null,
    elementType,
    status: normalizePlanningStatus(raw.status),
    startDate,
    date: startDate,
    startTime,
    time: startTime,
    endDate: raw.endDate || raw.end_date || toDateKey(calculatedEnd),
    endTime: isAllDay ? '' : String(raw.endTime || raw.end_time || endTimeFallback).slice(0, 5),
    durationMinutes,
    progress: Math.min(100, Math.max(0, toFiniteNumber(raw.progress))),
    estimatedHours: Math.max(0, toFiniteNumber(raw.estimatedHours ?? raw.estimated_hours)),
    completedHours: Math.max(0, toFiniteNumber(raw.completedHours ?? raw.completed_hours)),
    remainingHours: Math.max(0, toFiniteNumber(raw.remainingHours ?? raw.remaining_hours)),
    isAllDay,
    isLocked: toPlanningBoolean(raw.isLocked ?? raw.is_locked),
    isBillable: toPlanningBoolean(raw.isBillable ?? raw.is_billable),
    projectId: normalizeEntityId(raw.projectId ?? raw.project_id ?? raw.interventionId ?? raw.intervention_id),
    taskId: normalizeEntityId(raw.taskId ?? raw.task_id),
    deliverableId: normalizeEntityId(raw.deliverableId ?? raw.deliverable_id),
    clientId: normalizeEntityId(raw.clientId ?? raw.client_id),
    resourceId: normalizeEntityId(raw.resourceId ?? raw.resource_id ?? raw.ownerUserId),
    externalEventId: normalizeEntityId(raw.externalEventId ?? raw.outlookId),
    syncStatus: normalizeSyncStatus(raw.syncStatus ?? raw.sync_status),
  };
  return normalizePlanningInterval(normalized);
};

export const toPlanningPayload = (event) => ({
  ...event,
  date: event.startDate,
  time: event.startTime,
  type: event.elementType,
  duration: `${String(Math.floor(event.durationMinutes / 60)).padStart(2, '0')}:${String(event.durationMinutes % 60).padStart(2, '0')}`,
  outlookId: event.externalEventId,
  syncStatus: event.syncStatus === 'synced' ? 'pending' : event.syncStatus,
});
